// src/pages/CollectionReport/components/exportCollectionReportCsv.js
import commaNumber from "comma-number";
import { formatToCompanyTimezone } from "../../../../lib/functions";

const format = commaNumber.bindWith(",", ".");

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const fmtNum = (val) => format(Number(val || 0).toFixed(2));
const fmtDate = (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-");

/**
 * @param {object} params
 * @param {Array}   params.list        - sales rows from Redux
 * @param {Array}   params.receipts    - receipts rows from Redux
 * @param {object}  params.filters     - for recordType check
 * @param {object}  params.authInfo    - for tax label, serial/warehouse flags
 * @param {string}  params.title
 * @param {string}  params.filename
 */
export const exportCollectionReportCsv = ({
  list,
  receipts,
  filters,
  authInfo,
  title = "Sales Paid & Collection Record",
  filename = "collection_report",
}) => {
  const isIndia   = authInfo?.country?.toLowerCase() === "india";
  const taxLabel  = isIndia ? "GST" : "VAT";
  const isWithItem = filters?.recordType === "with item";

  // ── Sales section ─────────────────────────────────────────────
  const salesHeaders = isWithItem
    ? [
        "No.", "VCH No", "Date", "Customer", "Item",
        ...(authInfo?.is_serial === "yes" ? ["Serials"] : []),
        ...(authInfo?.is_warehouse === "yes" ? ["Warehouse"] : []),
        "Rate", `Discount%`, `${taxLabel} %`, "QTY", "Total",
        "Payment Cond", "Due", "Received",
      ]
    : [
        "No.", "Voucher No", "Date", "Customer / Debtor", "Employee",
        "Created By", "Mode of Pay", "Sub Total", "Discount",
        taxLabel, "T/Charge", "Total", "Due", "Paid",
      ];

  const salesRows = list.map((row, i) => {
    if (isWithItem) {
      const d = row.details?.[0] || {};
      return [
        i + 1,
        row.sale_voucher_no || "",
        fmtDate(row.created_date),
        row.acc_name || "",
        d.item_name || "",
        ...(authInfo?.is_serial === "yes"
          ? [d.is_serial === "yes" ? (d.serials?.map(s => s.serial_number).join("; ") || "") : ""]
          : []),
        ...(authInfo?.is_warehouse === "yes" ? [d.name || ""] : []),
        fmtNum(d.item_rate),
        row.is_cal_type === "individual" ? `${d.item_discount_per}%` : "",
        row.is_cal_type === "individual" ? `${d.item_tax_per}%` : "",
        d.qty_display || "",
        fmtNum(d.item_total),
        `${row.trans?.map(t => t.to_acc_name).join(", ") || ""} ${row.is_cond === "yes" ? "By Condition" : ""}`.trim(),
        fmtNum(row.due_amount),
        fmtNum(row.paid_amount),
      ].map(escape);
    } else {
      return [
        i + 1,
        row.sale_voucher_no || "",
        fmtDate(row.created_date),
        row.acc_name || "",
        row.employee_name || "",
        row.full_name || "",
        `${row.trans?.map(t => t.to_acc_name).join(", ") || ""} ${row.is_cond === "yes" ? "By Condition" : ""}`.trim(),
        fmtNum(row.sub_total),
        row.is_cal_type === "on_total" ? fmtNum(row.total_discount) : "---",
        row.is_cal_type === "on_total" ? fmtNum(row.total_tax) : "---",
        fmtNum(row.total_transport_cost),
        fmtNum(row.total_amount),
        fmtNum(row.due_amount),
        fmtNum(row.paid_amount),
      ].map(escape);
    }
  });

  // Sales totals row
  let salesTotalRow = [];
  if (isWithItem) {
    const masterQty = list.reduce((s, r) => s + r.details.reduce((ds, d) => ds + Number(d.item_qty || 0), 0), 0);
    const baseQty   = list.reduce((s, r) => s + r.details.reduce((ds, d) => ds + Number(d.retail_qty || 0), 0), 0);
    const total     = list.reduce((s, r) => s + Number(r.total_amount || 0), 0);
    const due       = list.reduce((s, r) => s + Number(r.due_amount || 0), 0);
    const paid      = list.reduce((s, r) => s + Number(r.paid_amount || 0), 0);
    const colCount  = salesHeaders.length;
    salesTotalRow = Array(colCount).fill("").map((_, idx) => {
      if (idx === 0) return "Grand Total";
      // QTY column — varies by serial/warehouse flags
      const qtyIdx = 10 + (authInfo?.is_serial === "yes" ? 1 : 0) + (authInfo?.is_warehouse === "yes" ? 1 : 0);
      if (idx === qtyIdx)     return `Master: ${format(masterQty.toFixed(2))} / Base: ${format(baseQty.toFixed(2))}`;
      if (idx === qtyIdx + 1) return fmtNum(total);
      if (idx === qtyIdx + 2) return "";
      if (idx === qtyIdx + 3) return fmtNum(due);
      if (idx === qtyIdx + 4) return fmtNum(paid);
      return "";
    }).map(escape);
  } else {
    const t = list.reduce((acc, row) => ({
      subtotal: acc.subtotal + Number(row.sub_total || 0),
      discount: acc.discount + (row.is_cal_type === "on_total" ? Number(row.total_discount || 0) : 0),
      vat:      acc.vat      + (row.is_cal_type === "on_total" ? Number(row.total_tax || 0) : 0),
      service:  acc.service  + Number(row.total_transport_cost || 0),
      total:    acc.total    + Number(row.total_amount || 0),
      due:      acc.due      + Number(row.due_amount || 0),
      paid:     acc.paid     + Number(row.paid_amount || 0),
    }), { subtotal: 0, discount: 0, vat: 0, service: 0, total: 0, due: 0, paid: 0 });

    salesTotalRow = [
      "Grand Total", "", "", "", "", "", "",
      fmtNum(t.subtotal), fmtNum(t.discount), fmtNum(t.vat),
      fmtNum(t.service), fmtNum(t.total), fmtNum(t.due), fmtNum(t.paid),
    ].map(escape);
  }

  // ── Receipts section ──────────────────────────────────────────
  const receiptsHeaders = [
    "No.", "VCH No", "Date", "Customer", "Receipt By",
    "Payment Method", "Received",
  ].map(escape);

  const receiptsRows = receipts.map((row, i) => [
    i + 1,
    row.rcv_code || "",
    fmtDate(row.created_date),
    row.acc_name || "",
    row.full_name || "",
    row.details?.[0]?.into_acc_name || "-",
    fmtNum(row.details?.[0]?.rcv_total),
  ].map(escape));

  const receiptsTotal = receipts.reduce((s, r) => s + Number(r.rcv_total || 0), 0);
  const receiptsTotalRow = [
    "Total Receipts", "", "", "", "", "",
    fmtNum(receiptsTotal),
  ].map(escape);

  // ── Grand combined total ──────────────────────────────────────
  const salesPaid = list.reduce((s, r) => s + Number(r.paid_amount || 0), 0);
  const grandTotal = salesPaid + receiptsTotal;

  // ── Assemble all sections ─────────────────────────────────────
  const allRows = [
    [title],
    [],

    // Sales section
    ...(list.length > 0 ? [
      ["=== Sales ==="],
      salesHeaders,
      ...salesRows,
      [],
      salesTotalRow,
      [],
    ] : []),

    // Receipts section
    ...(receipts.length > 0 ? [
      ["=== Receipts ==="],
      receiptsHeaders,
      ...receiptsRows,
      [],
      receiptsTotalRow,
      [],
    ] : []),

    // Combined grand total
    ...(list.length > 0 && receipts.length > 0 ? [
      [`Grand Total (Sales Paid & Previous Collection): ${fmtNum(grandTotal)}`],
    ] : []),
  ];

  const csv  = allRows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};